import { useRouter } from 'next/router'
import { useState, useReducer, useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import * as Sentry from '@sentry/nextjs'

import GrainButton from '@/shared/GrainButton'
import TextField from '@/UI/DataFields/TextField'
import IconButton from '@/shared/IconButton'
import Callout from '@/shared/Callout'
import FreeTrialLinkBanner from '@/shared/FreeTrialLinkBanner'

import useFreeTrialLink from '@/hooks/useFreeTrialLink'

import {
  loginRequest, registerEmailForTokenHook, validateTokenHook,
} from '@/api/orchestrator'
import { getUserFromIdHook } from '@/api/get-user-from-id-request'
import { sendResetPasswordEmailRequest, sendUnlockAccountEmailRequest } from '@/api/dfr'

import { initialState, requestReducer } from '@/lib/reducers'
import {
  fetching, success, error, reset,
} from '@/lib/action-creators'
import {
  LOGIN_EMAIL_CTA_CLICKED, LOGIN_2FA_CTA_CLICKED, LOGIN_PASSWORD_CTA_CLICKED, LOGIN_EVENT,
} from '@/lib/constants'
import gridAnalytics from '@/lib/analytics'

import { isEmail, isLoginPasswordValid } from '@/utils/utils'
import { storeAuthCookie, syncLocaleCookie } from '@/utils/cookieUtil'
import {
  LOGIN_ERROR,
  normalizeErrorResponse,
  getErrorTitle,
  getErrorSubtext,
  getComplexErrorItems,
} from '@/utils/errorUtil'
import {
  storeTokens, storeUserProfile, getUserProfile, storeWorkflowSummary,
} from '@/utils/localStorageUtil'
import { getLandingRouteForProfile } from '@/utils/landingRouteUtil'
import { getProfileLocale } from '@/utils/languageUtil'
import { getFreeTrialTraits } from '@/utils/freeTrialUtil'
import { withUtmCampaign } from '@/utils/utmCampaignUtil'
import {
  ArrowSVGIcon,
  EyeSVGIcon,
  SidePanelIcon,
  ClosedEyeSVGIcon,
} from '@/components/icons'
import { useBranding, useIsBrandingPending } from '@/layout/branding-theme'
import { BrandedSidePanel } from '@/layout/branded-side-panel'
import { AccountLocked } from './account-locked'
import { ForgotPassword } from './forgot-password'
import Login2FA from './login-2fa'

type LoginProps = {
  notice?: {
    title: string
    caption: string
  }
}

function Login({ notice }: LoginProps) {
  const router = useRouter()
  const { t } = useTranslation(['login', 'common'], { nsMode: 'fallback' })
  const branding = useBranding()
  const isBrandingPending = useIsBrandingPending()

  const profile = getUserProfile()

  // Set by the `/free-trial/:uuid` deep link — the banner follows the visitor
  // through every step of the login flow until they reach the main page.
  const freeTrialId = useFreeTrialLink()

  const [email, setEmail] = useState(null)
  const [otpCode, setOTPCode] = useState({})
  const [password, setPassword] = useState(null)

  const [currentStep, setCurrentStep] = useState(0)

  const [showPassword, setShowPassword] = useState(false)

  const [isForgotPassword, setIsForgetPassword] = useState(false)
  const [isAccountLocked, setAccountLocked] = useState(false)

  const onPrivacyPolicyClick = () => {
    window.open('https://gridverify.com/privacy-policy', '_blank')
    window.focus()
  }

  const [loginRequestState, loginDispatch] = useReducer(requestReducer, initialState)

  const {
    response: loginResponse,
    isFetching: isLoginFetching,
    error: loginError,
  } = loginRequestState

  const {
    refetch: getUser,
    data: userData,
    isLoading: isUserLoading,
  } = getUserFromIdHook(profile?.userId)

  const {
    mutate: registerEmail,
    isLoading: isRegisteringEmail,
    reset: resetRegisterEmail,
    error: registerEmailError,
    isSuccess: isRegisterEmailSuccess,
  } = registerEmailForTokenHook()

  const emailError = registerEmailError ? normalizeErrorResponse(registerEmailError) : null

  const {
    mutate: validateToken,
    reset: resetValidateToken,
    error: validateError,
    isSuccess: isValidateSuccess,
  } = validateTokenHook()

  useEffect(() => {
    if (isValidateSuccess) {
      setCurrentStep(currentStep + 1)
      resetValidateToken()
    }
    if (isRegisterEmailSuccess) {
      setCurrentStep(currentStep + 1)
      resetRegisterEmail()
    }
  }, [isValidateSuccess, isRegisterEmailSuccess])

  useEffect(() => {
    if (currentStep === 1 && Object.values(otpCode).join('').length === 6) {
      validateToken({ email, payload: { '2fa': Object.values(otpCode).join('') } })
      gridAnalytics.track(LOGIN_2FA_CTA_CLICKED)
    }
  }, [otpCode])

  useEffect(() => {
    if (loginResponse && !userData) {
      if (loginResponse.isPasswordUpdateRequired) {
        router.push({ pathname: '/reset-password', query: { requestId: loginResponse.token, type: 'update_password' } })
      } else {
        getUser(loginResponse.userId)
      }
    }
  }, [loginResponse])

  useEffect(() => {
    if (userData) {
      storeWorkflowSummary({ workflows: userData.customer.workflows })
      storeUserProfile({
        ...profile,
        ...userData,
        teamName: userData.customer.name,
        customerId: userData.customer.id,
        email: userData.email,
        hasViewedTutorial: userData.hasViewedTutorial,
      })

      syncLocaleCookie(userData)
      router.push(getLandingRouteForProfile(userData), undefined, { locale: getProfileLocale(userData) })
      loginDispatch(reset())
    }
  }, [userData])

  useEffect(() => {
    if (loginError?.code === 'account_locked') {
      setAccountLocked(true)
    }
  }, [loginError])

  const onLoginPressed = async event => {
    event.preventDefault()
    loginDispatch(fetching())

    try {
      const response = await loginRequest(withUtmCampaign({ email, password }))

      if (response.isPasswordUpdateRequired) {
        loginDispatch(success(response))
        return
      }
      storeTokens(response)
      storeUserProfile({ userId: response.userId })
      storeAuthCookie()

      Sentry.setUser({
        email,
        id: response.userId,
      })
      Sentry.setContext('User', {
        customer_id: response.customerId ?? '',
      })

      loginDispatch(success(response))

      gridAnalytics.identify(response.userId, {
        email,
        customer_id: response.customerId ?? '',
        ...getFreeTrialTraits(),
      })

      gridAnalytics.group(response.customerId ?? '')

      gridAnalytics.track(LOGIN_EVENT, {
        method: 'email',
        customer_id: response.customerId ?? '',
      })
    } catch (e) {
      loginDispatch(error(e))
    }
    gridAnalytics.track(LOGIN_PASSWORD_CTA_CLICKED)
  }

  const [
    resetPasswordRequestState, resetPasswordDispatch,
  ] = useReducer(requestReducer, initialState)

  const onResetPasswordPressed = async event => {
    event.preventDefault()
    resetPasswordDispatch(fetching())

    try {
      const response = await sendResetPasswordEmailRequest({ email })
      resetPasswordDispatch(success(response.message))
    } catch (e) {
      resetPasswordDispatch(error(LOGIN_ERROR))
    }
  }

  const [
    unlockAccountRequestState, unlockAccountDispatch,
  ] = useReducer(requestReducer, initialState)

  const onUnlockAccountPressed = async event => {
    event.preventDefault()
    unlockAccountDispatch(fetching())

    try {
      const response = await sendUnlockAccountEmailRequest({ email })
      unlockAccountDispatch(success(response.message))
    } catch (e) {
      unlockAccountDispatch(error(LOGIN_ERROR)) // ?
    }
  }

  const handleSubmitEmail = event => {
    event.preventDefault()
    registerEmail(email)
    gridAnalytics.track(LOGIN_EMAIL_CTA_CLICKED)
  }

  const renderSpecialStep = () => {
    if (isForgotPassword) {
      return (
        <ForgotPassword
          onBackToLoginClick={() => setIsForgetPassword(false)}
          onResetPasswordPressed={onResetPasswordPressed}
          requestState={resetPasswordRequestState}
          email={email}
        />
      )
    }
    return (
      <AccountLocked
        onBackToLoginClick={() => {
          setAccountLocked(false)
          setCurrentStep(0)
        }}
        onUnlockAccountPressed={onUnlockAccountPressed}
        requestState={unlockAccountRequestState}
        email={email}
      />
    )
  }

  const renderStep = () => {
    if (currentStep === 0) {
      return (
        <>
          <div className="step-header flex max-h-[88px] min-h-[88px] items-center justify-start gap-6 border-b border-b-gray-5 p-6">
            <div className="top-text">
              <h1 className="mb-0">{t('log_in')}</h1>
              <div className="caption-text text-gray-3">
                {t('access_existing_account')}
              </div>
            </div>
            <GrainButton
              type="button"
              text={t('create_account')}
              variant="secondary"
              onClick={() => router.push('/create-account')}
              className="create-account-button ml-auto"
            />
          </div>
          <form onSubmit={handleSubmitEmail}>
            <div className="step-body flex flex-col gap-6 p-6">
              <>
                {notice && (
                  <Callout
                    title={notice.title}
                    smallCaption={notice.caption}
                    isError
                  />
                )}
                <TextField
                  label={t('email_address')}
                  placeholder={t('email_placeholder')}
                  autoComplete="email"
                  onChange={e => setEmail(e.target.value)}
                  required
                  type="email"
                  autoFocus
                />
                { emailError && (
                  <Callout
                    title={getErrorTitle(emailError)}
                    smallCaption={getErrorSubtext(emailError)}
                    errorItems={getComplexErrorItems(emailError)}
                    isError
                  />
                )}
              </>
            </div>

            <div className="step-footer flex gap-2 border-t border-t-gray-5 p-6 [&_button]:flex-[1_1_0px]">
              <GrainButton
                text={t('continue')}
                variant="primary"
                className="continue-email-button [&_svg]:[transform:rotate(180deg)]"
                icon={<ArrowSVGIcon />}
                isDisabled={!(isEmail(email))}
                isLoading={isRegisteringEmail}
                fullWidth
                type="submit"
              />
            </div>
          </form>
        </>
      )
    } if (currentStep === 1) {
      return (
        <>
          <div className="step-header flex max-h-[88px] min-h-[88px] items-center justify-start gap-6 border-b border-b-gray-5 p-6">
            <GrainButton
              type="button"
              text={t('back')}
              variant="secondary"
              onClick={() => setCurrentStep(currentStep - 1)}
              icon={<ArrowSVGIcon />}
              iconLeft
            />
            <h1 className="mb-0">{t('confirm_your_email')}</h1>

          </div>
          <div className="step-body flex flex-col gap-6 p-6">
            <Login2FA
              label={`${t('enter_code_sent_to')} ${email}`}
              setOTPCode={setOTPCode}
              error={validateError as any}
              email={email}
            />
          </div>
        </>
      )
    } if (currentStep === 2) {
      return (
        <>
          <div className="step-header flex max-h-[88px] min-h-[88px] items-center justify-start gap-6 border-b border-b-gray-5 p-6">
            <GrainButton
              type="button"
              text={t('back')}
              variant="secondary"
              onClick={() => {
                loginDispatch(reset())
                setCurrentStep(0)
              }}
              icon={<ArrowSVGIcon />}
              iconLeft
            />
            <div className="top-text">
              <h1 className="mb-0">{t('welcome_back')}</h1>
              <div className="caption-text text-gray-3">
                {t('log_into_existing_account')}
              </div>
            </div>

          </div>
          <form onSubmit={onLoginPressed}>
            <div className="step-body flex flex-col gap-6 p-6">
              <p>
                {t('enter_password_for')}
                {' '}
                {email}
              </p>
              <div className="password-field flex items-center justify-center [&_.field-container]:grow">
                <TextField
                  placeholder={t('password_placeholder')}
                  onChange={e => setPassword(e.target.value)}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="password"
                  required
                  autoFocus
                />

                <IconButton
                  icon={showPassword ? <EyeSVGIcon /> : <ClosedEyeSVGIcon />}
                  variant="secondary"
                  size="small"
                  onClick={() => setShowPassword(!showPassword)}
                  className="eye-button ml-2"
                />
              </div>
              { loginError && (
              <Callout
                title={getErrorTitle(loginError)}
                smallCaption={getErrorSubtext(loginError)}
                errorItems={getComplexErrorItems(loginError)}
                isError
              />
              )}
            </div>
            <div className="step-footer flex gap-2 border-t border-t-gray-5 p-6 [&_button]:flex-[1_1_0px]">
              <GrainButton
                text={t('forgot_password')}
                variant="secondary"
                onClick={() => setIsForgetPassword(true)}
              />
              <GrainButton
                text={t('continue')}
                variant="primary"
                className="continue-email-button [&_svg]:[transform:rotate(180deg)]"
                icon={<ArrowSVGIcon />}
                isDisabled={!(isLoginPasswordValid(password))}
                isLoading={isLoginFetching || isUserLoading}
                type="submit"
              />
            </div>
          </form>
        </>
      )
    }

    return <></>
  }

  const showMobileScreen = window.innerWidth <= 768

  const showPrivacyPolicy = currentStep === 0 && !isForgotPassword && !isAccountLocked

  return (
    <div className="log-in-page flex h-full w-full items-center bg-gray-7 p-6">
      { !showMobileScreen && !isBrandingPending && (branding ? <BrandedSidePanel className="fixed inset-y-0 left-0 right-[54%]" /> : (
        <div className="grid-svg-container fixed right-[54%] flex h-full items-center">
          <SidePanelIcon className="grid-svg" />
        </div>
      ))}

      <div className="log-in-column relative ml-[48%] flex w-[520px] flex-col gap-3 [&_.log-in]:ml-0 [&_.log-in]:w-full [&_.forgot-password]:ml-0 [&_.forgot-password]:w-full" data-testid="log-in-column">
        { isForgotPassword || isAccountLocked ? (
          <>{ renderSpecialStep() }</>
        ) : (

          <div
            // These input variants also reach the OTP inputs Login2FA renders in step 1,
            // which is why login-2fa.tsx marks their 56px height important.
            className="log-in relative rounded-xl border border-gray-1 bg-gray-6-filled [box-shadow:var(--form-shadow)] [&_.step-body_.field-container_input]:h-[46px] [&_.step-body_.field-container_input]:rounded-xl [&_.step-body_.field-container_input]:border [&_.step-body_.field-container_input]:border-solid [&_.step-body_.field-container_input]:border-gray-5 [&_.step-body_.field-container_input]:p-4 [&_.step-body_.field-container_input]:text-gray-1"
            data-testid="log-in-card"
          >
            {renderStep()}
          </div>
        )}

        { freeTrialId && <FreeTrialLinkBanner variant="login" /> }

        { showPrivacyPolicy && (
          <div
            // Hangs off the column rather than the card, so it sits 16px below
            // whichever is last — the card, or the free trial banner however
            // many lines its subtitle wraps to — without moving the card itself.
            className="privacy-policy absolute inset-x-0 top-full mt-4 text-justify font-[Switzer] text-[12px] font-normal not-italic leading-[110%] text-gray-4"
          >
            {t('privacy_policy_text')}
            {' '}
            {' '}
            <a href="" onClick={onPrivacyPolicyClick} className="in-text-link text-gray-4">{t('privacy_policy')}</a>
            {t('privacy_policy_opt_out')}
          </div>
        )}
      </div>
    </div>
  )
}

export default Login
